/**
 * studio/src/stores/confirm.ts
 *
 * Pinia store for the in-app confirmation modal. The modal component
 * (AppConfirmModal.vue) subscribes to `current`; any view calls
 * `request(opts)` and `await`s the returned `Promise<boolean>`.
 *
 * Why this exists (added 2026-06-28): the studio used `window.confirm()`
 * for pitch deletion. Cursor's browser automation can auto-accept
 * browser-native dialogs, so the delete prompt was effectively
 * unreliable. The store-backed modal is in-DOM, so it can't be
 * bypassed by automation.
 *
 * Single-open invariant: a second `request()` while a modal is open
 * rejects. We deliberately do NOT queue — the studio's destructive
 * flows are guarded by archive preconditions and disabled buttons,
 * so a "two confirmations open at once" state would mean a UI bug
 * that should surface, not silently queue.
 */

import { defineStore } from "pinia";
import { ref } from "vue";

export type ConfirmVariant = "default" | "warning" | "danger";

export interface ConfirmOptions {
  title: string;
  body?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ConfirmVariant;
  /** When true, the modal renders the destructive styling + the
   *  acknowledgement input below the body. The confirm button stays
   *  disabled until the input matches `acknowledgePhrase`. */
  destructive?: boolean;
  /** Exact string the user must type to enable the destructive
   *  confirm button. Convention: the literal word `"delete"` for
   *  hard-delete flows (matches GitHub / Docker). */
  acknowledgePhrase?: string;
  /** Optional test id forwarded to the modal root. Useful for
   *  Vue Test Utils + Playwright. */
  testId?: string;
}

export const useConfirmStore = defineStore("confirm", () => {
  const current = ref<ConfirmOptions | null>(null);
  // The pending resolver. We intentionally allow only one at a time
  // — see the file docstring. Set by `request()`, cleared by `resolve()`.
  let resolver: ((value: boolean) => void) | null = null;

  function request(opts: ConfirmOptions): Promise<boolean> {
    if (current.value) {
      return Promise.reject(
        new Error(
          "A confirmation is already open. Concurrent confirm() calls indicate a UI bug — surface it, don't queue it.",
        ),
      );
    }
    current.value = opts;
    return new Promise<boolean>((resolve) => {
      resolver = resolve;
    });
  }

  function resolve(value: boolean): void {
    const r = resolver;
    resolver = null;
    current.value = null;
    r?.(value);
  }

  return { current, request, resolve };
});